import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Key } from 'lucide-react'
import { api, type DarkWebCredentialStatus, type DarkWebPerson } from '../../lib/api'
import { Button, Drawer, ErrorBanner, StatusChip } from '../../components/ui'
import { SOURCE_LABEL, STATUS_LABEL, STATUS_TONE, formatDay, isOpenStatus, sourceTypeColor, sourceTypeLabel } from './format'

type Exposure = Awaited<ReturnType<typeof api.getDarkWebPersonDetail>>['exposures'][number]

/**
 * Everything about one exposed person, with the remediation actions. Actions apply to all of
 * the person's exposures in this category.
 */
export function PersonDrawer({ person, canEdit, onClose, onStatus, onAssign, onCreateTask }: {
  person: DarkWebPerson | null
  canEdit: boolean
  onClose: () => void
  onStatus: (person: DarkWebPerson, status: DarkWebCredentialStatus, note?: string) => Promise<void>
  onAssign: (person: DarkWebPerson) => void
  onCreateTask: (person: DarkWebPerson) => void
}) {
  const [exposures, setExposures] = useState<Exposure[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  async function load(email: string) {
    setExposures(null)
    setError(null)
    try {
      const res = await api.getDarkWebPersonDetail(email)
      if (res.success) setExposures(res.exposures)
      else setError(res.error || 'The server returned an error.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network error.')
    }
  }

  useEffect(() => {
    setNote('')
    if (person) load(person.email)
  }, [person?.email])

  async function act(status: DarkWebCredentialStatus, defaultNote?: string) {
    if (!person) return
    setSaving(true)
    try {
      await onStatus(person, status, note.trim() || defaultNote)
      setNote('')
      await load(person.email)
    } finally {
      setSaving(false)
    }
  }

  const open = person ? isOpenStatus(person.status) : false
  const notes = [...new Set((exposures || []).map(e => e.notes).filter(Boolean))] as string[]

  return (
    <Drawer
      open={!!person}
      onClose={onClose}
      title={person?.email || ''}
      description={person && (
        <span className="flex flex-wrap items-center gap-2">
          <StatusChip tone={STATUS_TONE[person.status] || 'critical'}>{STATUS_LABEL[person.status] || person.status}</StatusChip>
          <span>Owner: {person.ownerName || 'Unassigned'}</span>
          {person.taskId && <Link to="/tasks?search=dark%20web%20exposure" className="text-link hover:underline">View task</Link>}
        </span>
      )}
      footer={canEdit && person && (
        <div className="flex w-full flex-col gap-3">
          <div>
            <label htmlFor="dw-person-note" className="mb-1 block text-[13px] font-medium text-ink-secondary">Note (optional)</label>
            <input id="dw-person-note" value={note} onChange={e => setNote(e.target.value)} maxLength={2000} placeholder="e.g. ticket number" className="h-[38px] w-full rounded-lg border border-line-strong bg-surface px-3 text-sm text-ink focus:outline-none focus-visible:ring-[3px] focus-visible:ring-focus" />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {open ? (
              <>
                <Button size="sm" onClick={() => onAssign(person)} disabled={saving}>Assign owner</Button>
                <Button size="sm" onClick={() => onCreateTask(person)} disabled={saving}>Create task</Button>
                <Button size="sm" onClick={() => act('false_positive')} disabled={saving}>False positive</Button>
                {person.status !== 'reset_requested' && <Button size="sm" onClick={() => act('reset_requested')} disabled={saving}>Mark reset requested</Button>}
                <Button size="sm" variant="primary" onClick={() => act('resolved', 'Password reset done')} loading={saving}>Password reset done</Button>
              </>
            ) : (
              <Button size="sm" onClick={() => act('new')} loading={saving}>Reopen</Button>
            )}
          </div>
        </div>
      )}
    >
      {error && <ErrorBanner title="Couldn't load this person" description={error} onRetry={() => person && load(person.email)} />}
      {!error && !exposures && <p className="text-sm text-muted" role="status">Loading…</p>}
      {exposures && (
        <div className="flex flex-col gap-4">
          {notes.length > 0 && (
            <div className="rounded-lg border border-line bg-surface-raised p-3 text-sm">
              <span className="font-semibold">Notes</span>
              <ul className="mt-1 list-disc pl-5 text-ink-secondary">{notes.map(n => <li key={n}>{n}</li>)}</ul>
            </div>
          )}
          {exposures.map(exposure => (
            <section key={exposure.id} className="rounded-xl border border-line p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 font-semibold">
                  <span className={`h-2.5 w-2.5 rounded-sm ${sourceTypeColor(exposure.sourceType)}`} aria-hidden="true" />
                  {sourceTypeLabel(exposure.sourceType)} · {SOURCE_LABEL[exposure.source] || exposure.source}
                </span>
                <StatusChip tone={STATUS_TONE[exposure.status] || 'critical'}>{STATUS_LABEL[exposure.status] || exposure.status}</StatusChip>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
                <div><dt className="text-muted">Latest leak</dt><dd className="m-0">{formatDay(exposure.latestLeakAt)}</dd></div>
                <div><dt className="text-muted">First found</dt><dd className="m-0">{formatDay(exposure.firstSeenAt)}</dd></div>
                <div><dt className="text-muted">Last seen in a scan</dt><dd className="m-0">{formatDay(exposure.lastSeenAt)}</dd></div>
                <div>
                  <dt className="text-muted">Password</dt>
                  <dd className="m-0">{exposure.hasPassword ? <span className="inline-flex items-center gap-1 font-medium text-critical"><Key className="h-3 w-3" aria-hidden="true" />Exposed</span> : 'Not exposed'}</dd>
                </div>
              </dl>
              {exposure.leaks.length > 0 && (
                <div className="mt-3">
                  <span className="text-[13px] text-muted">{exposure.sourceType === 'breach' ? 'Breaches' : 'Sites'}</span>
                  <div className="mt-1 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
                    {exposure.leaks.map(leak => <span key={leak} className="rounded-md bg-surface-raised px-2 py-0.5 font-mono text-xs text-ink-secondary">{leak}</span>)}
                  </div>
                </div>
              )}
              {exposure.dataClasses.length > 0 && (
                <p className="mt-2 text-[13px] text-ink-secondary">Data exposed: {exposure.dataClasses.join(', ')}</p>
              )}
            </section>
          ))}
        </div>
      )}
    </Drawer>
  )
}
